import { FC } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HomeIcon, PlusIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import ModernSpaceCard from "./ModernSpaceCard";
import Pagination from "shared/Pagination/Pagination";
import { Location } from "data/types";

interface ModernSpacesGridProps {
  loading: boolean;
  locations: Location[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  isCustomer?: boolean;
  onPageChange: (page: number) => void;
  onAddSpace: () => void;
  onDeleteSpace?: (id: string) => void;
}

const ModernSpacesGrid: FC<ModernSpacesGridProps> = ({
  loading,
  locations,
  totalCount,
  totalPages,
  currentPage,
  isCustomer = false,
  onPageChange,
  onAddSpace,
  onDeleteSpace,
}) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 20,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
    },
    exit: {
      opacity: 0,
      y: -20,
      scale: 0.95,
    },
  };

  const EmptyState = () => (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6 }}
      className="flex flex-col items-center justify-center py-20"
    >
      <div className="w-32 h-32 bg-gradient-to-br from-emerald-100 to-blue-100 dark:from-emerald-900/30 dark:to-blue-900/30 rounded-full flex items-center justify-center mb-6">
        <HomeIcon className="w-16 h-16 text-emerald-500 dark:text-emerald-400" />
      </div>

      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">Aucun espace pour le moment</h3>

      <p className="text-gray-600 dark:text-gray-400 text-center max-w-md mb-8 leading-relaxed">
        Commencez à partager vos espaces avec la communauté EventToBee. Ajoutez votre premier espace dès
        maintenant.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-2xl mb-8">
        {[
          { icon: PlusIcon, title: "Ajoutez", desc: "Votre espace unique" },
          { icon: DocumentTextIcon, title: "Complétez", desc: "Les informations" },
          { icon: HomeIcon, title: "Partagez", desc: "Avec la communauté" },
        ].map((step, index) => {
          const Icon = step.icon;
          return (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}
              className="text-center p-4"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-blue-500 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Icon className="w-6 h-6 text-white" />
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-1">{step.title}</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">{step.desc}</p>
            </motion.div>
          );
        })}
      </div>

      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={onAddSpace}
        className="px-8 py-3 bg-gradient-to-r from-emerald-600 to-blue-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
      >
        Ajouter mon premier espace
      </motion.button>
    </motion.div>
  );

  const LoadingState = () => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex flex-col items-center justify-center py-20"
    >
      <div className="relative mb-6">
        <div className="w-16 h-16 border-4 border-emerald-200 dark:border-emerald-800 border-t-emerald-600 dark:border-t-emerald-400 rounded-full animate-spin"></div>
        <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-r-blue-400 dark:border-r-blue-500 rounded-full animate-spin animation-delay-75"></div>
      </div>

      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Chargement de vos espaces</h3>

      <p className="text-gray-600 dark:text-gray-400">Veuillez patienter quelques instants...</p>
    </motion.div>
  );

  if (loading) {
    return <LoadingState />;
  }

  if (totalCount === 0) {
    return <EmptyState />;
  }

  return (
    <div className="space-y-6">
      {/* Spaces Grid */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
      >
        <AnimatePresence mode="wait">
          {locations.map((location) => (
            <motion.div
              key={`${location.id}-${currentPage}`}
              variants={cardVariants}
              transition={{ duration: 0.5, ease: "easeOut" }}
              layout
            >
              <ModernSpaceCard location={location} isCustomer={isCustomer} onDelete={onDeleteSpace} />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      {/* Modern Pagination */}
      {totalPages > 1 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex justify-center mt-12"
        >
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-lg border border-gray-200 dark:border-gray-700">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={onPageChange} />
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default ModernSpacesGrid;
